/**
 * Copyright © 2026 Adnuntius AS.
 */
import {interpolateTemplate} from '../../common/filters/interpolate-template';

// OAuth redirect_uri shape check. Accept HTTPS anywhere (server-side web clients like claude.ai use https://claude.ai/api/mcp/auth_callback) and loopback HTTP
// for native CLI clients (RFC 8252 §7.3 — Claude Code, Codex CLI, mcp-remote bridge all bind a random port on 127.0.0.1). The authoritative security check is
// the DCR-registered redirect_uri allowlist enforced server-side at /v1/apikeys/authorize-code — this regex is a sanity guard against egregiously wrong URIs.
export const VALID_OAUTH_REDIRECT = /^(https:\/\/[^\s?#]+|http:\/\/(127\.0\.0\.1|localhost)(:\d+)?(\/[^?#]*)?)$/;

export function isOAuthConsentRequest(params: any): boolean {
  return !!(params && params.code_challenge && VALID_OAUTH_REDIRECT.test(params.redirect_uri || '') && params.client_id);
}

export function isOAuthConsentDestination(toState?: {name?: string} | null, toParams?: any): boolean {
  return toState?.name === 'nude.mcp-authorize' || isOAuthConsentRequest(toParams);
}

// Query params the authorization endpoint and nude consent page must both declare so UI-Router exposes the handshake.
export const OAUTH_CONSENT_PARAMS = {
  app: null, callback: null,
  code_challenge: null, code_challenge_method: null, redirect_uri: null, state: null, client_id: null, resource: null
};

// Capped at 24h server-side; 1h is the default lease for agent integrations.
export const AGENT_LEASE_HOURS = 1;

export const API_KEY_SCOPE_OPTIONS = [
  'STATS_QUERY',
  'ADVERTISING_QUERY',
  'ADVERTISING_UPDATE',
  'PUBLISHING_QUERY',
  'REACH_QUERY',
  'SEGMENT_UPLOAD',
  'LOG_DOWNLOAD',
  'BIDDER'
];

export function oauthErrorMessage(data: any): string {
  // A 422 nests the real reason under errors[]; its top-level text is only the generic "produced validation errors" wrapper.
  const nested = data?.errors?.[0]?.error;
  const validationText = nested?.text ? interpolateTemplate(nested.text)(nested.parameters) : undefined;
  return (data && (data.error_description || validationText || data.text || data.message)) || 'Authorisation failed.';
}
